import { resolvePath } from 'utils/helpers';
import { Endpoints } from 'utils/paths';
import { clientApi } from '../api';

interface GetGiftcardByIdParams {
  giftcardId: string;
  token?: string;
  redeemToken?: string;
}

export const getGiftcardById = async (params: GetGiftcardByIdParams) => {
  const { giftcardId, redeemToken, token } = params;

  const url = resolvePath({
    pathname: Endpoints.GIFTCARD_DETAILS,
    query: { giftcardId },
  });

  const response = await clientApi.get<AdaptedGiftcard>(url, {
    params: { redeemToken, token },
  });

  return response.data;
};

interface GetGiftcardDepreciationSchedulesParams {
  giftcardId: string;
  token?: string;
  redeemToken?: string;
}

export const getGiftcardDepreciationSchedules = async (
  params: GetGiftcardDepreciationSchedulesParams,
) => {
  const { giftcardId, redeemToken, token } = params;

  const url = resolvePath({
    pathname: Endpoints.GIFTCARD_DEPRECIATION_SCHEDULES,
    query: { giftcardId },
  });

  const response = await clientApi.get<AdaptedGiftcardDepreciation[]>(url, {
    params: { redeemToken, token },
  });

  return response.data;
};

interface GetGiftcardTransactionsParams {
  giftcardId: string;
  token?: string;
  page?: number | string;
  pageSize?: number | string;
}

export const getGiftcardTransactions = async (
  params: GetGiftcardTransactionsParams,
) => {
  const { giftcardId, token, page, pageSize } = params;

  const url = resolvePath({
    pathname: Endpoints.GIFTCARD_TRANSACTIONS,
    query: { giftcardId },
  });

  const response = await clientApi.get<
    Paginated<AdaptedGiftcardTransactionIntent | AdaptedGiftcardTransaction>
  >(url, {
    params: { token, page, pageSize },
  });

  return response.data;
};

interface GetGiftcardTransactionParams {
  giftcardId: string;
  transactionId: string;
  token?: string;
}

export const getGiftcardTransaction = async (
  params: GetGiftcardTransactionParams,
): Promise<AdaptedGiftcardTransaction> => {
  const { giftcardId, transactionId, token } = params;

  const url = resolvePath({
    pathname: Endpoints.GIFTCARD_TRANSACTIONS_DETAILS,
    query: { giftcardId, transactionId },
  });

  const response = await clientApi.get<AdaptedGiftcardTransaction>(url, {
    params: { token },
  });

  return response.data;
};

interface GetGiftcardInfoParams {
  id: string;
  shortId: string;
  networkId: string;
}

export const getGiftcardInfo = async (params: GetGiftcardInfoParams) => {
  const { id, shortId, networkId } = params;

  const url = resolvePath({
    pathname: Endpoints.GIFTCARD_INFO,
    query: { giftcardId: id },
  });

  const response = await clientApi.get<AdaptedGiftcard>(url, {
    params: { id, shortId, networkId },
  });

  return response.data;
};

interface GetGiftcardByUrlParams {
  giftcardUrl: string;
}

export const getGiftcardByUrl = async (params: GetGiftcardByUrlParams) => {
  const { giftcardUrl } = params;

  const response = await clientApi.get<AdaptedGiftcard>('giftcards/parseUrl', {
    params: { giftcardUrl },
  });

  return response.data;
};

interface CreateSecretLinkParams {
  giftcardId: string;
  redeemToken?: string;
  token?: string;
  email: string;
}

export const createSecretLink = async (params: CreateSecretLinkParams) => {
  const { giftcardId, email, redeemToken, token } = params;

  const url = resolvePath({
    pathname: Endpoints.SEND_GIFTCARD_SECRET_LINK,
    query: { giftcardId },
  });

  const response = await clientApi.post<{ success: true }>(url, {
    redeemToken,
    token,
    email,
  });

  return response.data;
};

interface SendGiftcardByEmailParams {
  giftcardId: string;
  token?: string;
  email: string;
}

export const sendGiftcardByEmail = async (
  params: SendGiftcardByEmailParams,
) => {
  const { giftcardId, email, token } = params;

  const url = resolvePath({
    pathname: Endpoints.SEND_EMAIL_TO_RECEIVER,
    query: { giftcardId },
  });

  const response = await clientApi.post<{ success: true }>(url, {
    token,
    email,
  });

  return response.data;
};

interface CreateGiftcardRedeem {
  idempotencyKey: string;
  retailerId: string;
  pincode?: string;
  giftcardId: string;
  accessToken: string;
  amount: number;
  receiptNumber: string;
}

export const createGiftcardRedeem = async (params: CreateGiftcardRedeem) => {
  const {
    idempotencyKey,
    retailerId,
    pincode,
    giftcardId,
    accessToken,
    amount,
    receiptNumber,
  } = params;

  const url = resolvePath({
    pathname: Endpoints.REDEEM_GIFTCARD,
    query: { giftcardId },
  });

  const response = await clientApi.post(url, {
    idempotencyKey,
    retailerId,
    pincode,
    giftcardId,
    accessToken,
    amount,
    receiptNumber,
  });

  return response.data;
};

interface CreateGiftcardActivation {
  idempotencyKey: string;
  retailerId: string;
  pincode?: string;
  giftcardId: string;
  accessToken: string;
  amount?: number;
  receiptNumber: string;
}

export const createGiftcardActivation = async (
  params: CreateGiftcardActivation,
) => {
  const {
    idempotencyKey,
    retailerId,
    pincode,
    giftcardId,
    accessToken,
    amount,
    receiptNumber,
  } = params;

  const url = resolvePath({
    pathname: Endpoints.ACTIVATE_GIFTCARD,
    query: { giftcardId },
  });

  const response = await clientApi.post(url, {
    idempotencyKey,
    retailerId,
    pincode,
    accessToken,
    amount,
    receiptNumber,
  });

  return response.data;
};

interface CreateSponsoredGiftcardActivation {
  idempotencyKey: string;
  giftcardId: string;
  redeemToken: string;
}

export const createSponsoredGiftcardActivation = async (
  params: CreateSponsoredGiftcardActivation,
) => {
  const { idempotencyKey, giftcardId, redeemToken } = params;

  const url = resolvePath({
    pathname: Endpoints.ACTIVATE_SPONSORED_GIFTCARD,
    query: { giftcardId },
  });

  const response = await clientApi.post(url, { idempotencyKey, redeemToken });

  return response.data;
};

interface GetGiftcardWalletParams {
  giftcardId: string;
  token: string;
}

interface GetGiftcardWalletsResponse {
  googlePassUrl: string;
  applePassUrl: string;
}

export const getGiftcardWallets = async (params: GetGiftcardWalletParams) => {
  const { giftcardId, token } = params;

  const response = await clientApi.get<GetGiftcardWalletsResponse>(
    'giftcardWallet',
    {
      params: { giftcardId, token },
    },
  );

  return response.data;
};

interface GetGiftcardPdfParams {
  giftcardId: string;
  token: string;
}

export const getGiftcardPdf = async (params: GetGiftcardPdfParams) => {
  const { giftcardId, token } = params;

  const url = resolvePath({
    pathname: Endpoints.GIFTCARD_PDF,
    query: { giftcardId },
  });

  const response = await clientApi.get<{ url: string }>(url, {
    params: { token },
  });

  return response.data;
};

interface ValidateRedeemPincodeParams {
  giftcardId: string;
  redeemToken: string;
  redeemPagePin: string;
}

export const validateRedeemPincode = async (
  params: ValidateRedeemPincodeParams,
) => {
  const { giftcardId, redeemPagePin, redeemToken } = params;

  const url = resolvePath({
    pathname: Endpoints.GIFTCARD_VALIDATE_REDEEM_PAGE_PIN,
    query: { giftcardId },
  });

  const response = await clientApi.get<{ validRedeemPagePin: boolean }>(url, {
    params: { redeemPagePin, redeemToken },
  });

  return response.data;
};
